import { createMemo, type JSX, onCleanup, Show } from 'solid-js';
import { t } from '../../i18n';
import type { LayoutNode } from '../../lib/terminal/layout';
import { TerminalPaneDivider } from './TerminalPaneDivider';

// The pane tree's recursive renderer (#4397, epic #4391) — a `LayoutNode`
// walked into nested `.terminal__split` boxes with a `TerminalPaneDivider`
// between the two sides, bottoming out at `renderPane(id)` (the caller's
// own `TerminalPane`, so this file knows nothing about panes' data). Pure
// presenter: every write goes back up through `onRatioChange`.
//
// WRAPPED IN A MEMO, ON PURPOSE — NOT DECORATION. Solid components run once;
// a plain recursive function call in a component's return value reads
// `props.root` a single time at mount and never again; a LATER split, close
// or ratio change would leave the DOM exactly as it was on mount. Reading
// `props.root` (and every `node.a`/`node.b`/`dir`/`ratio` the walk touches)
// inside `createMemo` is what makes the whole subtree rebuild whenever the
// tree changes upstream. Cost: EVERY `root` write — including a ratio drag's
// own per-pointermove ticks — rebuilds the full tree, tearing down and
// remounting every pane's own `TerminalLog` (its scroll position, its follow
// state). Split/close are infrequent clicks, so that cost is invisible there;
// a continuous drag is the one case it is not, and it is accepted rather than
// hidden — `TerminalPaneDivider`'s own docblock names it as a rough edge, not
// silently shipped as if it were smooth.
//
// THE TREE MEMO TRACKS `root` ONLY, through its own identity memo. `props.root`
// is a getter over the WHOLE layout signal, so reading it directly would
// rebuild on a focus or zoom write too — and the pane title bar writes focus
// on `pointerdown`, replacing the toolbar button under the pointer before its
// `click` can fire (#4901).
//
// ZOOM SHORT-CIRCUITS THE WHOLE TREE. A zoomed pane renders ALONE — the tree
// is not walked at all, so a zoomed pane's siblings neither mount nor keep
// their scroll position; re-entering the split view re-mounts them exactly
// the way switching routes does elsewhere in this app. Simplest correct
// reading of "one pane rendered full-window" (`layout.ts`'s own `zoomId`
// docstring), and it keeps this recursion from having to thread a
// zoom-in-progress flag through every level.

type Path = readonly ('a' | 'b')[];

function renderNode(
  node: LayoutNode,
  path: Path,
  onRatioChange: (path: Path, ratio: number) => void,
  renderPane: (id: string) => JSX.Element,
  holdDrag: (end: () => void) => () => void,
): JSX.Element {
  if (node.kind === 'pane') return renderPane(node.id);
  const isRow = node.dir === 'row';
  return (
    <div class="terminal__split" classList={{ 'terminal__split--row': isRow }}>
      <div class="terminal__split-side" style={{ flex: `${node.ratio} 1 0%` }}>
        {renderNode(node.a, [...path, 'a'], onRatioChange, renderPane, holdDrag)}
      </div>
      <TerminalPaneDivider
        dir={node.dir}
        ratio={node.ratio}
        onChange={(ratio) => onRatioChange(path, ratio)}
        label={t('terminal.pane_resize_divider')}
        holdDrag={holdDrag}
      />
      <div class="terminal__split-side" style={{ flex: `${1 - node.ratio} 1 0%` }}>
        {renderNode(node.b, [...path, 'b'], onRatioChange, renderPane, holdDrag)}
      </div>
    </div>
  );
}

export function TerminalPaneTree(props: {
  readonly root: LayoutNode;
  readonly zoomId: string | null;
  readonly onRatioChange: (path: Path, ratio: number) => void;
  readonly renderPane: (id: string) => JSX.Element;
}): JSX.Element {
  const root = createMemo(() => props.root);
  // Every drag in flight — two pointers can drag two dividers at once. This
  // component is the stable owner a drag outlives its divider into
  // (`TerminalPaneDivider`'s `holdDrag`), so its cleanup is where an unmount
  // mid-drag removes the window listeners; a drag that ends on its own leaves.
  const drags = new Set<() => void>();
  onCleanup(() => {
    for (const end of [...drags]) end();
  });
  const holdDrag = (end: () => void): (() => void) => {
    drags.add(end);
    return () => drags.delete(end);
  };
  const tree = createMemo(() =>
    renderNode(root(), [], props.onRatioChange, props.renderPane, holdDrag),
  );

  return (
    <Show when={props.zoomId} fallback={<div class="terminal__pane-tree">{tree()}</div>}>
      {(id) => props.renderPane(id())}
    </Show>
  );
}
